import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import { KV, KVRow } from '../components/KV';
import { get_form_def } from '../app_configs';
import type {
  ConfigPayload,
  ConfigValue,
  FormDefinition,
  ToggleDefinition,
} from '../app_configs/types';
import SelectFieldRow from '../app_configs/SelectFieldRow';

interface AppUserRow {
  tenant_id: string;
  tenant_name: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  user_status: string;
}

interface TenantRow {
  tenant_id: string;
  name: string;
}
interface AppTenantRow {
  tenant_id: string;
  name: string;
  user_count: number;
}

// Apps whose data describes the TENANT (one shared record for all its users), not a
// single user — provisioned/edited on TenantDetail, so the per-user "Users" view is
// empty for them (issue #15). AppDetail shows a "Tenants" view instead.
const TENANT_SCOPED_APPS = new Set(['captomi_office']);
interface UserRow {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface AppRow {
  app_code: string;
  display_name: string;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  created_by: string;
}

interface ChangeRow {
  change_id: string;
  changed_at: string | null;
  changed_by: string;
  config: Record<string, unknown>;
  note: string | null;
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Compute defaults from a FormDefinition. Used to pre-fill the form when the
// config doc is absent (create flow) per SPEC §6.
function defaults_from_form(form: FormDefinition): ConfigPayload {
  const out: ConfigPayload = {};
  for (const t of form.toggles) {
    if (t.type === 'select') {
      // A select owns multiple keys — seed them from its default option's `set`.
      const def = t.options.find((o) => o.id === t.default_id) ?? t.options[0];
      if (def) Object.assign(out, def.set);
    } else {
      out[t.key] = t.default;
    }
  }
  return out;
}

// Which destructive toggles flipped in the direction that triggers confirmation?
function destructive_changes(
  form: FormDefinition,
  before: ConfigPayload | null,
  after: ConfigPayload,
): ToggleDefinition[] {
  const out: ToggleDefinition[] = [];
  for (const t of form.toggles) {
    if (t.type === 'select') continue; // selects own no single key + are non-destructive
    if (!t.destructive) continue;
    const prev = before?.[t.key];
    const next = after[t.key];
    if (t.destructive.when === 'enabled') {
      if (t.type === 'boolean' && prev === false && next === true) out.push(t);
    } else {
      // 'changed' — fire on any change, including the create case (prev undefined).
      if (prev !== next) out.push(t);
    }
  }
  return out;
}

export default function AppDetail() {
  const { app_code = '' } = useParams<{ app_code: string }>();
  const qc = useQueryClient();

  const appQuery = useQuery({
    queryKey: ['apps', app_code],
    queryFn: async () => {
      // No dedicated GET /api/apps/:app_code in v1 — read from the list and find.
      const list = await api.get<{ apps: AppRow[] }>('/api/apps');
      const row = list.apps.find((a) => a.app_code === app_code);
      if (!row) throw new ApiError(404, 'not_found');
      return row;
    },
  });

  const configQuery = useQuery({
    queryKey: ['apps', app_code, 'config'],
    queryFn: () =>
      api.get<{ config: ConfigPayload | null }>(`/api/apps/${app_code}/config`),
    enabled: !!appQuery.data,
  });

  const changesQuery = useQuery({
    queryKey: ['apps', app_code, 'changes'],
    queryFn: () =>
      api.get<{ changes: ChangeRow[] }>(`/api/apps/${app_code}/changes`),
    enabled: !!appQuery.data,
  });

  const is_tenant_scoped = TENANT_SCOPED_APPS.has(app_code);

  const usersQuery = useQuery({
    queryKey: ['apps', app_code, 'users'],
    queryFn: () => api.get<{ users: AppUserRow[] }>(`/api/apps/${app_code}/users`),
    enabled: !!appQuery.data && !is_tenant_scoped,
  });

  const tenantsQuery = useQuery({
    queryKey: ['apps', app_code, 'tenants'],
    queryFn: () => api.get<{ tenants: AppTenantRow[] }>(`/api/apps/${app_code}/tenants`),
    enabled: !!appQuery.data && is_tenant_scoped,
  });

  const plansQuery = useQuery({
    queryKey: ['apps', app_code, 'config', 'plans'],
    queryFn: () =>
      api.get<{ config: Record<string, unknown> | null }>(
        `/api/apps/${app_code}/config/plans`,
      ),
    enabled: !!appQuery.data && app_code === 'captomi_office',
  });

  const form = useMemo(() => get_form_def(app_code), [app_code]);
  const doc_exists = configQuery.data?.config != null;

  return (
    <div className="container">
      <p style={{ marginTop: 0 }}>
        <Link to="/apps" className="muted">
          ← Apps
        </Link>
      </p>

      {appQuery.isLoading && <p>Loading app...</p>}
      {appQuery.error && (
        <p className="error">
          Failed to load app:{' '}
          {appQuery.error instanceof ApiError ? appQuery.error.reason : String(appQuery.error)}
        </p>
      )}
      {appQuery.data && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <h1 style={{ margin: 0 }}>{appQuery.data.display_name}</h1>
            <span
              style={{
                color: appQuery.data.status === 'active' ? 'var(--green-700)' : 'var(--muted)',
                fontSize: 13,
              }}
            >
              {appQuery.data.status}
            </span>
          </div>
          <p className="muted" style={{ marginTop: 0, marginBottom: 16, fontSize: 13 }}>
            <code>{appQuery.data.app_code}</code> · created by {appQuery.data.created_by} ·
            updated {format_dt(appQuery.data.updated_at)}
          </p>

          <h2 style={{ marginTop: 24 }}>Config</h2>
          {configQuery.isLoading && <p>Loading config...</p>}
          {configQuery.data && (
            <ConfigSection
              app_code={app_code}
              form={form}
              current={configQuery.data.config}
              on_saved={() => {
                qc.invalidateQueries({ queryKey: ['apps', app_code, 'config'] });
                qc.invalidateQueries({ queryKey: ['apps', app_code, 'changes'] });
              }}
            />
          )}
          {!form && doc_exists && configQuery.data && (
            <RawJsonView config={configQuery.data.config!} />
          )}

          <h2 style={{ marginTop: 32 }}>Recent changes</h2>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {changesQuery.isLoading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
            {changesQuery.data && (
              <DataTable
                rows={changesQuery.data.changes}
                columns={[
                  { header: 'When', cell: (c) => format_dt(c.changed_at) },
                  { header: 'Who', cell: (c) => c.changed_by },
                  { header: 'Note', cell: (c) => c.note ?? <span className="muted">—</span> },
                  {
                    header: 'Snapshot',
                    cell: (c) => (
                      <details>
                        <summary style={{ cursor: 'pointer', fontSize: 13 }}>view</summary>
                        <pre
                          style={{
                            fontSize: 12,
                            background: 'var(--green-50)',
                            padding: 8,
                            borderRadius: 4,
                            overflowX: 'auto',
                          }}
                        >
                          {JSON.stringify(c.config, null, 2)}
                        </pre>
                      </details>
                    ),
                  },
                ]}
                empty_message="No changes recorded yet."
              />
            )}
          </div>

          {app_code === 'captomi_office' && (
            <PlansCatalogSection
              loading={plansQuery.isLoading}
              catalog={plansQuery.data?.config ?? null}
            />
          )}

          {is_tenant_scoped ? (
            <TenantsSection
              tenants={tenantsQuery.data?.tenants ?? []}
              loading={tenantsQuery.isLoading}
            />
          ) : (
            <UsersSection
              app_code={app_code}
              users={usersQuery.data?.users ?? []}
              loading={usersQuery.isLoading}
              on_provisioned={() =>
                qc.invalidateQueries({ queryKey: ['apps', app_code, 'users'] })
              }
            />
          )}
        </>
      )}
    </div>
  );
}

interface TierDef {
  price_usd?: number;
  limits?: { minutes?: number; messages?: number };
}

// Read-only render of the captomi-office plans catalog (docs/admin.md §7.7).
// Catalog shape (apps/captomi_office/config/plans, seeded by captomi-office's
// seed-plans-catalog.mjs): { <TRADE>: { <tier>: { price_usd, limits } }, addon: { <TRADE>: { minutes, messages } } }.
function PlansCatalogSection({
  loading,
  catalog,
}: {
  loading: boolean;
  catalog: Record<string, unknown> | null;
}) {
  const addon =
    catalog && typeof catalog.addon === 'object' && catalog.addon !== null
      ? (catalog.addon as Record<string, { minutes?: number; messages?: number }>)
      : null;
  const trades = catalog
    ? Object.entries(catalog).filter(
        ([k, v]) => k !== 'addon' && v !== null && typeof v === 'object',
      )
    : [];

  return (
    <>
      <h2 style={{ marginTop: 32 }}>Plans catalog</h2>
      <p className="muted" style={{ marginTop: -8, fontSize: 13 }}>
        Read-only — <code>apps/captomi_office/config/plans</code>, seeded by captomi-office.
        Drives the derived billing price.
      </p>
      <div className="card">
        {loading && <p style={{ margin: 0 }}>Loading...</p>}
        {!loading && !catalog && (
          <p className="muted" style={{ margin: 0 }}>
            No plans catalog has been seeded yet.
          </p>
        )}
        {!loading && catalog && trades.length === 0 && !addon && (
          <p className="muted" style={{ margin: 0 }}>
            Catalog is empty.
          </p>
        )}
        {!loading &&
          trades.map(([trade, tiers]) => (
            <div key={trade} style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 8px' }}>{trade}</h4>
              <DataTable
                rows={Object.entries(tiers as Record<string, unknown>).map(([tier, def]) => {
                  const d = (def ?? {}) as TierDef;
                  return {
                    tier,
                    price_usd: d.price_usd,
                    minutes: d.limits?.minutes,
                    messages: d.limits?.messages,
                  };
                })}
                columns={[
                  { header: 'Tier', cell: (r) => <strong>{r.tier}</strong> },
                  {
                    header: 'Price',
                    cell: (r) => (r.price_usd != null ? `$${r.price_usd}/mo` : '—'),
                  },
                  { header: 'Minutes', cell: (r) => r.minutes ?? '—' },
                  { header: 'Messages', cell: (r) => r.messages ?? '—' },
                ]}
                empty_message="No tiers."
              />
            </div>
          ))}
        {!loading && addon && (
          <div>
            <h4 style={{ margin: '0 0 8px' }}>Add-on units (per grant)</h4>
            <DataTable
              rows={Object.entries(addon).map(([trade, a]) => ({
                trade,
                minutes: a?.minutes,
                messages: a?.messages,
              }))}
              columns={[
                { header: 'Trade', cell: (r) => <strong>{r.trade}</strong> },
                { header: 'Minutes', cell: (r) => r.minutes ?? '—' },
                { header: 'Messages', cell: (r) => r.messages ?? '—' },
              ]}
              empty_message="No add-ons."
            />
          </div>
        )}
      </div>
    </>
  );
}

// Tenant-scoped apps (e.g. captomi_office) have one app-data record per TENANT, not per
// user — so we list the tenants that have it. Editing lives on TenantDetail; there's no
// per-user provision here (that path is 410-gated for these apps, issue #15).
function TenantsSection({
  tenants,
  loading,
}: {
  tenants: AppTenantRow[];
  loading: boolean;
}) {
  return (
    <>
      <h2 style={{ marginTop: 32 }}>Tenants</h2>
      <p className="muted" style={{ marginTop: -8, fontSize: 13 }}>
        Tenant-scoped app — its data is one shared record per tenant (covering all the
        tenant's users). Open a tenant to view or edit it.
      </p>
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
        {loading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {!loading && (
          <DataTable
            rows={tenants}
            columns={[
              {
                header: 'Tenant',
                cell: (t) => (
                  <Link to={`/tenants/${t.tenant_id}`}>
                    <strong>{t.name}</strong>
                  </Link>
                ),
              },
              { header: 'Users', cell: (t) => t.user_count },
            ]}
            empty_message="No tenants have this app yet."
          />
        )}
      </div>
    </>
  );
}

function UsersSection({
  app_code,
  users,
  loading,
  on_provisioned,
}: {
  app_code: string;
  users: AppUserRow[];
  loading: boolean;
  on_provisioned: () => void;
}) {
  const [show_provision, set_show_provision] = useState(false);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 32 }}>
        <h2 style={{ margin: 0 }}>Users</h2>
        <button
          className="secondary"
          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
          onClick={() => set_show_provision((v) => !v)}
        >
          {show_provision ? 'Cancel' : 'Provision a user'}
        </button>
      </div>
      {show_provision && (
        <ProvisionUserPanel
          app_code={app_code}
          on_done={() => {
            set_show_provision(false);
            on_provisioned();
          }}
        />
      )}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
        {loading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {!loading && (
          <DataTable
            rows={users}
            columns={[
              {
                header: 'Tenant',
                cell: (u) => (
                  <Link to={`/tenants/${u.tenant_id}`}>{u.tenant_name}</Link>
                ),
              },
              {
                header: 'User',
                cell: (u) => (
                  <Link to={`/tenants/${u.tenant_id}/users/${u.user_id}`}>
                    <strong>
                      {u.first_name} {u.last_name}
                    </strong>
                  </Link>
                ),
              },
              { header: 'Email', cell: (u) => u.email },
              { header: 'Status', cell: (u) => u.user_status },
            ]}
            empty_message="No users have this app yet."
          />
        )}
      </div>
    </>
  );
}

function ProvisionUserPanel({
  app_code,
  on_done,
}: {
  app_code: string;
  on_done: () => void;
}) {
  const [tenant_id, set_tenant_id] = useState('');
  const [user_id, set_user_id] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: TenantRow[] }>('/api/tenants'),
  });
  const usersQuery = useQuery({
    queryKey: ['tenant', tenant_id, 'users'],
    queryFn: () =>
      api.get<{ users: UserRow[] }>(`/api/tenants/${tenant_id}/users`),
    enabled: !!tenant_id,
  });

  async function provision() {
    set_busy(true);
    set_error(null);
    try {
      // The typed-form registry was retired with issue #15 WS3 — no app has a typed
      // skeleton anymore, so this always provisions a blank doc (the consumer edits it
      // as raw JSON afterward).
      await api.post(`/api/tenants/${tenant_id}/users/${user_id}/apps`, { app_code });
      on_done();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Provisions <code>{app_code}</code> for the selected user with its default
        skeleton. Edit the data on the user's page afterward.
      </p>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Tenant</span>
        <select
          value={tenant_id}
          onChange={(e) => {
            set_tenant_id(e.target.value);
            set_user_id('');
          }}
        >
          <option value="">— select tenant —</option>
          {(tenantsQuery.data?.tenants ?? []).map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      {tenant_id && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>User</span>
          <select value={user_id} onChange={(e) => set_user_id(e.target.value)}>
            <option value="">— select user —</option>
            {(usersQuery.data?.users ?? []).map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.first_name} {u.last_name} · {u.email}
              </option>
            ))}
          </select>
        </label>
      )}
      {error && <div className="error">{error}</div>}
      <div>
        <button onClick={provision} disabled={busy || !tenant_id || !user_id}>
          {busy ? 'Provisioning...' : 'Provision'}
        </button>
      </div>
    </div>
  );
}

function ConfigSection({
  app_code,
  form,
  current,
  on_saved,
}: {
  app_code: string;
  form: FormDefinition | null;
  current: ConfigPayload | null;
  on_saved: () => void;
}) {
  // Four-state UI (SPEC §6):
  //   - has form + has current → form pre-filled, Save button
  //   - has form + no current → form pre-filled with defaults, Create button
  //   - no form + has current → raw JSON viewer (rendered above by parent)
  //   - no form + no current → placeholder
  if (!form && !current) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          (no toggles configured — define the form in{' '}
          <code>admin/ui/src/app_configs/{app_code}.tsx</code>)
        </p>
      </div>
    );
  }
  if (!form) {
    // raw JSON viewer is rendered above the recent-changes table by the parent.
    return null;
  }
  return (
    <ConfigForm
      app_code={app_code}
      form={form}
      current={current}
      on_saved={on_saved}
    />
  );
}

function RawJsonView({ config }: { config: ConfigPayload }) {
  return (
    <div className="card">
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Raw JSON (no form definition registered for this app — edit via the API or
        publish a form definition).
      </p>
      <pre
        style={{
          fontSize: 12,
          background: 'var(--green-50)',
          padding: 12,
          borderRadius: 4,
          overflowX: 'auto',
          margin: 0,
        }}
      >
        {JSON.stringify(config, null, 2)}
      </pre>
    </div>
  );
}

function ConfigForm({
  app_code,
  form,
  current,
  on_saved,
}: {
  app_code: string;
  form: FormDefinition;
  current: ConfigPayload | null;
  on_saved: () => void;
}) {
  const initial = useMemo(
    () => current ?? defaults_from_form(form),
    [current, form],
  );
  const [values, set_values] = useState<ConfigPayload>(initial);
  const [note, set_note] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [confirm_open, set_confirm_open] = useState(false);
  const [confirm_text, set_confirm_text] = useState('');

  // Reset when the underlying data changes (e.g. another admin saved).
  useEffect(() => {
    set_values(initial);
  }, [initial]);

  const pending_destructive = destructive_changes(form, current, values);
  const is_create = current === null;

  function set_value(key: string, v: ConfigValue) {
    set_values((prev) => ({ ...prev, [key]: v }));
  }

  async function do_save() {
    set_busy(true);
    set_error(null);
    try {
      await api.put(`/api/apps/${app_code}/config`, {
        config: values,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      set_note('');
      set_confirm_open(false);
      set_confirm_text('');
      on_saved();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_busy(false);
    }
  }

  function on_submit(e: FormEvent) {
    e.preventDefault();
    if (pending_destructive.length > 0) {
      set_confirm_open(true);
      return;
    }
    void do_save();
  }

  return (
    <form onSubmit={on_submit} className="card" style={{ display: 'grid', gap: 18 }}>
      {form.toggles.map((t) =>
        t.type === 'select' ? (
          <SelectFieldRow
            key={t.id}
            field={t}
            values={values}
            on_change={(set) => set_values((prev) => ({ ...prev, ...set }))}
          />
        ) : (
          <ToggleField
            key={t.key}
            t={t}
            value={values[t.key]}
            on_change={(v) => set_value(t.key, v)}
          />
        ),
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Note (optional)</span>
        <input
          value={note}
          onChange={(e) => set_note(e.target.value)}
          maxLength={500}
          placeholder="e.g. disable provisioning while 10DLC pending"
        />
      </label>
      {error && <div className="error">{error}</div>}
      <div>
        <button type="submit" disabled={busy}>
          {busy ? 'Saving...' : is_create ? 'Create config' : 'Save'}
        </button>
      </div>

      {confirm_open && (
        <ConfirmModal
          pending={pending_destructive}
          confirm_text={confirm_text}
          on_change_text={set_confirm_text}
          on_cancel={() => {
            set_confirm_open(false);
            set_confirm_text('');
          }}
          on_confirm={() => void do_save()}
          busy={busy}
        />
      )}
    </form>
  );
}

function ToggleField({
  t,
  value,
  on_change,
}: {
  t: ToggleDefinition;
  value: ConfigValue | undefined;
  on_change: (v: ConfigValue) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <KV>
        <KVRow
          label={t.label}
          value={
            t.type === 'boolean' ? (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={value === true}
                  onChange={(e) => on_change(e.target.checked)}
                />
                <span className="muted" style={{ fontSize: 13 }}>
                  {value === true ? 'enabled' : 'disabled'}
                </span>
              </label>
            ) : t.type === 'number' ? (
              <input
                type="number"
                value={(value as number | undefined) ?? ''}
                onChange={(e) => on_change(Number(e.target.value))}
              />
            ) : (
              <input
                type="text"
                value={(value as string | undefined) ?? ''}
                onChange={(e) => on_change(e.target.value)}
              />
            )
          }
        />
      </KV>
      {t.description && (
        <p className="muted" style={{ fontSize: 12, margin: 0, paddingLeft: 0 }}>
          {t.description}
        </p>
      )}
      {t.destructive && (
        <p style={{ fontSize: 12, color: '#b58900', margin: 0 }}>
          ⚠ Destructive — Save will require confirmation.
        </p>
      )}
    </div>
  );
}

function ConfirmModal({
  pending,
  confirm_text,
  on_change_text,
  on_cancel,
  on_confirm,
  busy,
}: {
  pending: ToggleDefinition[];
  confirm_text: string;
  on_change_text: (v: string) => void;
  on_cancel: () => void;
  on_confirm: () => void;
  busy: boolean;
}) {
  const ok = confirm_text === 'CONFIRM';
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 26, 20, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={on_cancel}
    >
      <div
        className="card"
        style={{ maxWidth: 540, width: '90%', background: 'var(--bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>Confirm destructive change</h3>
        <ul style={{ paddingLeft: 18 }}>
          {pending.map((t) => (
            <li key={t.key} style={{ marginBottom: 6 }}>
              <strong>{t.label}</strong>
              <div className="muted" style={{ fontSize: 13 }}>
                {t.destructive?.confirm_message}
              </div>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 13 }}>
          Type <code>CONFIRM</code> to proceed:
        </p>
        <input
          autoFocus
          value={confirm_text}
          onChange={(e) => on_change_text(e.target.value)}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="secondary" onClick={on_cancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={on_confirm} disabled={!ok || busy}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
